import { encrypt } from '@/common/securityUtils';
import Select from '@/components/Select';
import { useMainpageGroupList } from '@/features/recommendation-management/main-page/hooks/useMainPageGroupList';

import { MainPageTable } from '@/features/recommendation-management/main-page/table';
import { useAttributes } from '@/hooks/useAttributes';
import { useDynamicLocale } from '@/hoslog-intl';
import { createFileRoute, SearchSchemaInput } from '@tanstack/react-router';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { z } from 'zod';

const defaultLocationValue = encrypt('1');
const locationIdParser = parseAsString.withDefault(defaultLocationValue);
const pageParser = parseAsInteger.withDefault(0);

const sortItemSchema = z.object({
  id: z.enum(['groupName', 'totalStores', 'createdDate', 'updatedDate', 'remark']),
  desc: z.boolean(),
});

const MainPageGroupSearchSchema = z.object({
  sort: z
    .preprocess((val) => {
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return undefined;
        }
      }
      return val;
    }, z.array(sortItemSchema))
    .optional(),
  locationId: z.any().transform((val) => (val ? String(val) : defaultLocationValue)),
  q: z
    .any()
    .transform((val) => (val == null ? undefined : String(val)))
    .optional(),
  page: z.coerce.number().positive().catch(0),
  fetch: z.coerce.number().positive().max(100).catch(10),
});

export const Route = createFileRoute('/(app)/recommendation-management/main-page/')({
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => MainPageGroupSearchSchema.parse(search),
});
function RouteComponent() {
  const { t } = useDynamicLocale();

  const [locationId, setLocationId] = useQueryState('locationId', locationIdParser);
  const [, setPage] = useQueryState('page', pageParser);
  const handleLocationChange = (value: string) => {
    void setLocationId(value);
    void setPage(0);
  };
  const { data: locationOptions = [] } = useAttributes('location');

  const { mainPageGroup, pagination, isLoading } = useMainpageGroupList();

  return (
    <>
      <div className="relative">
        <div className="absolute right-0 -top-[65px] flex gap-3">
          <Select
            name="Location"
            options={locationOptions}
            triggerLabel={t('common.form.label.editorsPickLocation')}
            labelInTrigger={true}
            value={locationId}
            onValueChange={handleLocationChange}
            triggerClassName="flex gap-2 font-bold rounded border shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] border-primary text-[16px] leading-[24px] px-4 py-3"
          />
        </div>
      </div>
      <MainPageTable data={mainPageGroup} pagination={pagination} loading={isLoading} locationId={locationId} />
    </>
  );
}
